<script setup lang="ts">
    import { computed, ref } from 'vue'
    import { boolCompare } from '@common/Common'
    import type { MeetingQuestionReviewReadModel, ScoringVoteEntry } from '@Evaluation/AlternativesSurveys/Domain/readmodels'
    import MeetingQuestionReviewDr from '@Evaluation/AlternativesSurveys/Components/MeetingQuestionReviewDr.vue'
    import MeetingQuestionReviewPc from '@Evaluation/AlternativesSurveys/Components/MeetingQuestionReviewPc.vue'
    import AlternativeDetailsDialog from '@Alternatives/Alternatives/Components/AlternativeDetailsDialog.vue'
    import { AlternativeDetailsDialogViewModel } from '@Alternatives/Alternatives/Components/AlternativeDetailsDialog'
    import CriterionDetailsDialog from '@Criteria/Hierarchies/Components/CriterionDetailsDialog.vue'
    import { CriterionDetailsDialogViewModel } from '@Criteria/Hierarchies/Components/CriterionDetailsDialog'

    /* ---------------- props / emits ---------------- */

    const props = defineProps<{
        propRm?: MeetingQuestionReviewReadModel | null
        propIsMeetingManager: boolean
    }>()

    const emit = defineEmits<{
        (e: 'update-vote-value', value: number | null): void
        (e: 'update-vote-comment', comment: string | null): void
    }>()

    /* ---------------- shortcuts ---------------- */

    const rm = computed(() => props.propRm ?? null)
    const isMeetingManager = computed(() => props.propIsMeetingManager)

    /* ---------------- dialogs ---------------- */

    const alternativeDetailsDialog = ref(new AlternativeDetailsDialogViewModel())
    const criterionDetailsDialog = ref(new CriterionDetailsDialogViewModel())

    /* ---------------- derived state ---------------- */

    const contextName = computed(() => rm.value?.CriterionName ?? null)

    const votesEntries = computed<ScoringVoteEntry[]>(() =>
        [...(rm.value?.VotesEntries ?? [])].sort((a, b) =>
            boolCompare(a.SurveyId != null, b.SurveyId != null)
        )
    )

    const value = computed(() => rm.value?.Value ?? null)
    const comment = computed(() => rm.value?.Comment ?? null)

    const scaleLevels = computed(() => rm.value?.ScaleLevels ?? [])

    const alternativeName = computed(() => rm.value?.AlternativeName ?? null)
    const firstAlternativeName = computed(() => rm.value?.FirstAlternativeName ?? null)
    const secondAlternativeName = computed(() => rm.value?.SecondAlternativeName ?? null)

    /* ---------------- actions ---------------- */

    function showAlternativeDetails()
    {
        if (!rm.value) return
        alternativeDetailsDialog.value.show(
            rm.value.AlternativeName,
            rm.value.AlternativeDescription
        )
    }

    function showFirstAlternativeDetails()
    {
        if (!rm.value) return
        alternativeDetailsDialog.value.show(
            rm.value.FirstAlternativeName,
            rm.value.FirstAlternativeDescription
        )
    }

    function showSecondAlternativeDetails()
    {
        if (!rm.value) return
        alternativeDetailsDialog.value.show(
            rm.value.SecondAlternativeName,
            rm.value.SecondAlternativeDescription
        )
    }

    function showCriterionDetails()
    {
        if (!rm.value) return
        criterionDetailsDialog.value.show(
            rm.value.CriterionName,
            rm.value.CriterionPath,
            rm.value.CriterionDescription
        )
    }

    function updateVoteValue(val: number | null)
    {
        emit('update-vote-value', val)
    }

    function updateVoteComment(comment: string | null)
    {
        emit('update-vote-comment', comment)
    }
</script>


<template>
    <div v-if="rm">
        <CriterionDetailsDialog :viewModel="criterionDetailsDialog" />

        <AlternativeDetailsDialog :viewModel="alternativeDetailsDialog" />

        <MeetingQuestionReviewDr v-if="rm.AlternativeId"
                                 :propIsMeetingManager="isMeetingManager"
                                 :propContextName="contextName"
                                 :propValue="value"
                                 :propComment="comment"
                                 :propVotesEntries="votesEntries"
                                 :propAlternativeName="alternativeName"
                                 :propScaleLevels="scaleLevels"
                                 @show-alternative-details="showAlternativeDetails"
                                 @show-criterion-details="showCriterionDetails"
                                 @update-vote-value="updateVoteValue"
                                 @update-vote-comment="updateVoteComment" />

        <MeetingQuestionReviewPc v-if="!rm.AlternativeId"
                                 :propIsMeetingManager="isMeetingManager"
                                 :propContextName="contextName"
                                 :propValue="value"
                                 :propComment="comment"
                                 :propVotesEntries="votesEntries"
                                 :propFirstAlternativeName="firstAlternativeName"
                                 :propSecondAlternativeName="secondAlternativeName"
                                 @show-first-alternative-details="showFirstAlternativeDetails"
                                 @show-second-alternative-details="showSecondAlternativeDetails"
                                 @show-criterion-details="showCriterionDetails"
                                 @update-vote-value="updateVoteValue"
                                 @update-vote-comment="updateVoteComment" />
    </div>
</template>
